import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Text from "../../utils/commonComponent/Custom/text";
import CommonForm from "../../utils/commonComponent/commonForm";
import { AddButton, CancelButton, SaveAndUpdate } from "../../utils/commonComponent/Custom/Button";
import { fetchQuery } from "./../../utils/commonComponent/fetched";
import axiosWithToken from "../../utils/commonComponent/axiosTokenConfig";
import { BASE_URL } from "../../global";
import { DisciplineOptions } from "../disciplineMaster/disiplineConst";
import MaterialTestingTable from "./materialTestingTable";
import { getPermissions } from "../rbac/hasAccess";
import { validateRequiredFields } from "../../utils/commonComponent/errorAlert";

const EditTestMaster = (props) => {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);
  const navigate = useNavigate();
  const [inputStates, setInputStates] = React.useState({});
  const [materialTestings, setMaterialTestings] = React.useState([]);
  const [originalTestings, setOriginalTestings] = React.useState([]);
  const [disgroupList, setDisGroupList] = React.useState([]);
  const [unitList, setunitList] = React.useState([]);
  const [equipmentList, setequipmentList] = React.useState([]);
  const [deletedIds, setDeletedIds] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [totalRecords, setTotalRecords] = React.useState(0);

  const inputFields = [
    {
      type: "Autocomplete",
      label: "Discipline *",
      stateKey: "discplineId",
      options: DisciplineOptions,
      valueKey: "id",
      getOptionLabelKey: "label",
      disabled: params.id != 0,
    },
    {
      type: "Autocomplete",
      label: "Group *",
      stateKey: "groupId",
      options: disgroupList,
      valueKey: "id",
      getOptionLabelKey: "groupName",
    },
    { type: "TextField", label: "Material Name *", stateKey: "MaterialName" },
    { type: "TextField", label: "Sub Type", stateKey: "subType" },
  ];

  
  const addMaterialTesting = () => {
    const newTesting = {
      id: Date.now(), // Temporary ID for new items
      scopeType: 1,
      testName: "",
      testMethod: "",
      materialId: +params.id,
      observationType: 1,
      isNew: true, // Flag to identify new items
    };
    setMaterialTestings((prev) => [...prev, newTesting]);
    setTotalRecords((prev) => prev + 1); // keep pagination count updated
  };

  const updateMaterialTestingField = (id, key, value) => {
    setMaterialTestings((prev) =>
      prev.map((mt) => (mt.id === id ? { ...mt, [key]: value } : mt))
    );
  };

  const deleteMaterialTesting = (id, isExisting = false) => {
    // Remove from UI
    setMaterialTestings((prev) => prev.filter((mt) => mt.id !== id));
    setTotalRecords((prev) => prev - 1);
    // If it's an existing record, store ID for deletion
    if (isExisting) {
      setDeletedIds((prev) => [...prev, id]);
    }
  };

  const updateInputObject = (key, value) => {
    setInputStates((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const fetchMaterialScopeDetails = async () => {
    try {
      const [materialScope] = await fetchQuery(`select * from materialScope where id = ${params.id}`);
      setInputStates(materialScope);
    } catch (error) {
      console.error("Error fetching material scope:", error);
    }
  };

  const fetchUnitList = async () => {
    try {
      const { discplineId, groupId } = inputStates;
      if (!discplineId || !groupId) return;
      const unitList = await fetchQuery(`select * from unit where disciplineId = ${discplineId} and groupId = ${groupId}`);
      setunitList(unitList);
    } catch (error) {
      console.error("Error fetching unit list:", error);
    }
  };

  const fetchEquipmentList = async () => {
    try {
      const equipmentList = await fetchQuery(`select * from equipmentsMaster where status = 1`);
      setequipmentList(equipmentList);
    } catch (error) {
      console.error("Error fetching equipment:", error);
    }
  };

  const fetchDisciplineDetails = async () => {
    try {
      if (!inputStates.discplineId) return;
      const groupList = await fetchQuery(`select * from discipline where name = ${inputStates.discplineId}`);
      setDisGroupList(groupList);
    } catch (error) {
      console.error("Error fetching discipline:", error);
    }
  };

  const fetchMaterialTestings = async () => {
    if (params.id == 0) return;
    const offset = page * pageSize;
    try {
      const [testListCount] = await fetchQuery(`SELECT COUNT(*) AS count FROM materialTestings WHERE status = 1 and materialId = ${params.id};`);
      const testings = await fetchQuery(`SELECT * FROM materialTestings WHERE status = 1 and materialId = ${params.id} LIMIT ${pageSize} OFFSET ${offset}`);
      const normalized = (testings || []).map((t) => ({
        ...t,
        masterEquipmentId: t.masterEquipmentId
          ? String(t.masterEquipmentId)
            .split(",")
            .map((id) => parseInt(id.trim()))
          : [],
      }));
      setMaterialTestings(normalized);
      setOriginalTestings(normalized);
      setTotalRecords(testListCount?.count || 0);
    } catch (error) {
      console.error("Error fetching material testings:", error);
    }
  };

  const handleMaterialTestingOperations = async (materialScopeId) => {
    const newTestings = materialTestings.filter((test) => test.isNew);
    const existingTestings = materialTestings.filter((test) => !test.isNew);

    try {
      //  Delete stored IDs
      if (deletedIds.length > 0) {
        const idString = deletedIds.join(",");

        await axiosWithToken.post(BASE_URL + "dynamic", {
          query: `UPDATE materialTestings SET status = -1 WHERE id IN (${idString});`
        });
      }
      // Handle new testings (POST)
      if (newTestings.length > 0) {
        const bulkPayload = newTestings.map((test) => {
          const { id, isNew, ...testPayload } = test;
          return {
            materialId: Number(materialScopeId),
            scopeType: test.scopeType || null,
            testName: test.testName || null,
            testMethod: test.testMethod || null,
            observationType: test.observationType || null,
            rangeLower: test.rangeLower || null,
            rangeHigher: test.rangeHigher || null,
            muValue: test.muValue || null,
            testValue: test.testValue || null,
            location: test.location || null,
            masterEquipmentId: test.masterEquipmentId?.join(",") || null,
            status: 1
          };
        });
        await axiosWithToken.post(BASE_URL + "materialTestings/bulk", bulkPayload);
      }

      // Handle existing testings (PATCH)
      const updateRequests = existingTestings
        .filter((test) => {
          const original = originalTestings.find((orig) => orig.id === test.id);
          if (!original) return true;
          return (
            original.scopeType !== test.scopeType ||
            original.testName !== test.testName ||
            original.testMethod !== test.testMethod ||
            original.observationType !== test.observationType ||
            original.rangeLower !== test.rangeLower ||
            original.rangeHigher !== test.rangeHigher ||
            original.muValue !== test.muValue ||
            original.testValue !== test.testValue ||
            original.location !== test.location ||
            JSON.stringify(original.masterEquipmentId || []) !==
            JSON.stringify(test.masterEquipmentId || [])
          );
        })
        .map((test) => {
          const { isNew, ...testPayload } = test;
          return axiosWithToken.patch(BASE_URL + `materialTestings/${test.id}`, {
            ...testPayload,
            masterEquipmentId: test.masterEquipmentId?.join(",") || "", // 🔹 convert array → string
          });
        });

      if (updateRequests.length > 0) {
        await Promise.all(updateRequests);
      }
    } catch (error) {
      console.error("Error saving material testings:", error);
      throw error;
    }
  };

  const onSubmit = async () => {
    try {
      const isEdit = params.id != 0;
      const url = BASE_URL + `materialScope`;

      // Step 1: Save or update materialScope
      const res = isEdit
        ? await axiosWithToken.patch(url + `/${params.id}`, inputStates)
        : await axiosWithToken.post(url, inputStates);
      const materialScopeId = isEdit ? params.id : res?.data?.insertId;

      if (!materialScopeId) {
        toast.error("Failed to retrieve materialScope ID.");
        return;
      }

      // Step 2: Handle material testings (create/update)
      await handleMaterialTestingOperations(materialScopeId);

      toast.success(isEdit ? "Test Master Updated!" : "Test Master Created!");

      if (params.id == 0) {
        setTimeout(() => navigate("/master/testMaster"), 1000);
      } else {
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (error) {
      console.error("Submit error:", error);
    }
  };

  const validateForm = () => {
    // show alert 
    const fieldsToValidate = [
      { value: inputStates.discplineId, label: "Select Discipline" },
      { value: inputStates.groupId, label: "Group is required" },
      { value: inputStates.MaterialName, label: "Material Name is required" }
    ];
    if (!validateRequiredFields(fieldsToValidate)) {
      return;
    }
    // Validate material testings
    for (const test of materialTestings) {
      if (!test.testName?.trim() || !test.testMethod?.trim()) {
        toast.error("Test Name and Test Method are required");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit();
    }
  };

  useEffect(() => {
    if (params.id != 0) {
      fetchMaterialScopeDetails();
    }
    fetchEquipmentList();
  }, [params]);

  useEffect(() => {
    if (params.id != 0) {
      fetchMaterialTestings();
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    if (inputStates.discplineId) {
      fetchDisciplineDetails();
      if (inputStates.groupId) {
        fetchUnitList();
      }
    }
  }, [inputStates.discplineId, inputStates.groupId]);

  return (
    <div>
      <Text variant="h3" bold className="text-blue-600">
        {params.id == 0
          ? "Create New Accreditation Scope"
          : `Update Accreditation Scope`}
      </Text>
      <br />
      <CommonForm
        inputStates={inputStates}
        updateInputObject={updateInputObject}
        inputFields={inputFields}
      />
      <br />

      <div className="mb-4">
        <div className="flex justify-end items-center mb-3">
          <AddButton
            onClick={addMaterialTesting}
            label="Add Material Testing"
          />
        </div>

        <MaterialTestingTable
          materialTestings={materialTestings}
          deleteMaterialTesting={deleteMaterialTesting}
          updateMaterialTestingField={updateMaterialTestingField} unitList={unitList}
          equipmentList={equipmentList}
          inputStates={inputStates}
          permissions={permissions}
          page={page}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={(e, newPage) => setPage(newPage)}
          onPageSizeChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </div>

      <div className="flex gap-10 mt-5 justify-end">
        <CancelButton
          id="btn-cancel"
          label="Cancel"
          outline
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        />
        {(permissions.save || permissions.allAccess) && (
          <SaveAndUpdate
            id="btn-add-outline"
            label={params.id == 0 ? "Save" : "Update"}
            onClick={handleSubmit}
          />
        )}
      </div>
    </div>
  );
};

export default EditTestMaster;
